import { useLocalObservable } from 'mobx-react-lite';
import { RefObject, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { CallMediaPermissionValue, IMediaPermissions } from '@gem/call/CallStore/Call.types';
import {
  CallStatus,
  IStatusCallChanged,
  PreviewDisplayMode,
  WaitingRoomAccessStatus,
  WaitingRoomMemberChangedStatus,
} from '@gem/call/Model/callModel.types';
import {
  disposeUnauthorizedCallsApi,
  initUnauthorizedCallsApi,
} from '@gem/call/WsApi/unauthorizedWsCallsApi/UnauthorizedWsCallsApi';
import { unauthorizedWsCallsApiDisconnect } from '@gem/call/WsApi/wsApi';
import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { unauthorizedLogin } from 'APP/Tasks/authorization/login/unauthorizedLogin';
import { callModel } from 'APP/model/model';
import { callAnalytics } from 'APP/packages/analytics/analytics';
import { ProjectUrls } from 'APP/packages/projectUrls/ProjectUrls';
import { RouterLink, RouterParams } from 'APP/router/constants';
import { buildPath } from 'APP/router/helpers/buildPath';
import { useClearGetParameters } from 'APP/router/hooks/useClearGetParameters';
import { ICallPreviewDevicesPresenter } from 'APP/unauthorized/CallPreview/CallPreview.devices.presenter';
import Entities from 'STORE';
import { MainCall } from 'STORE/Calls/MainCall/MainCall';

import { UnauthorizedRouts } from '../UnauthorizedView.constants';

export interface ICallPreviewPresenter {
  isDarkMode: boolean;
  startCallRef: RefObject<() => Promise<void>>;
  ongoingCall?: MainCall | null;
  selfName: string;
  isLoading: boolean;
  isOpenedAlertGuestsDisabled: boolean;
  isUnauthorizedApiInitialized: boolean;
  onStartCall: (() => Promise<void>) | null;
  waitingRoomMemberStatus: WaitingRoomMemberChangedStatus | null;
  isWaitingRoomEnabled: boolean | null;
  previewDisplayMode: PreviewDisplayMode | null;
  disposeRoomListenerFunc: (() => void) | null;
  mediaPermissions: IMediaPermissions;
  hasMediaPermissions: boolean;
  isPreviewInitialized: boolean;
  isShowDefaultPreview: boolean;
  isShowWebinarPreview: boolean;
  onMediaPermissionsChanged(value: IMediaPermissions): void;
  requestWaitingRoomAccess(status: WaitingRoomAccessStatus): void;
  onStart(): void;
  onCancel(): Promise<void>;
  startCall(): Promise<void>;
  checkToStart(): void;
  initWaitingRoom(): void;
  disposeRoomListener(): void;
  showAccessDeniedAndRedirect(): Promise<void>;
  showGuestsDisabledAndRedirect(): Promise<void>;
  showCallFinishedAndRedirect(): Promise<void>;
}

interface CallPreviewPresenterProps {
  onStartCallRef: RefObject<() => Promise<void>>;
  devicesPresenter: ICallPreviewDevicesPresenter;
}

export const useCallPreviewPresenter = (
  props: CallPreviewPresenterProps
): ICallPreviewPresenter => {
  const params = useParams<{ channelId?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const clearDeepLinkParameters = useClearGetParameters(
    [
      RouterParams.deepLink,
      RouterParams.type,
      RouterParams.entityId,
      RouterParams.parentId,
      RouterParams.expired,
      RouterParams.branchMatchId,
      RouterParams.branchReferrer,
      RouterParams.source,
      RouterParams.channelId,
      RouterParams.invitationUrl,
      RouterParams.allowGuests,
    ],
    true
  );

  const presenter = useLocalObservable<ICallPreviewPresenter>(() => ({
    startCallRef: props.onStartCallRef,
    isUnauthorizedApiInitialized: false,
    disposeRoomListenerFunc: null,
    isWaitingRoomEnabled: null,
    waitingRoomMemberStatus: null,
    previewDisplayMode: null,
    onStartCall: null,
    isLoading: false,
    isOpenedAlertGuestsDisabled: false,
    mediaPermissions: {
      audio: CallMediaPermissionValue.Initial,
      video: CallMediaPermissionValue.Initial,
    },

    requestWaitingRoomAccess(status: WaitingRoomAccessStatus): void {
      callModel.requestWaitingRoomAccess({
        channelId: params!.channelId!,
        status,
      });
    },

    get isDarkMode(): boolean {
      return Entities.appearance.isDark;
    },

    get hasMediaPermissions(): boolean {
      return (
        presenter.mediaPermissions.audio !== CallMediaPermissionValue.Initial &&
        presenter.mediaPermissions.video !== CallMediaPermissionValue.Initial
      );
    },

    get ongoingCall(): MainCall | null {
      return Entities.Calls.ongoingCall;
    },

    get selfName(): string {
      return Entities.callInvitation.selfName || '';
    },

    get isPreviewInitialized(): boolean {
      return presenter.previewDisplayMode !== null;
    },

    get isShowDefaultPreview(): boolean {
      return presenter.previewDisplayMode === PreviewDisplayMode.DefaultPreview;
    },

    get isShowWebinarPreview(): boolean {
      return presenter.previewDisplayMode === PreviewDisplayMode.WebinarPreview;
    },

    async onStart(): Promise<void> {
      if (
        (presenter.isWaitingRoomEnabled &&
          presenter.waitingRoomMemberStatus === WaitingRoomMemberChangedStatus.Approved) ||
        !presenter.isWaitingRoomEnabled
      ) {
        presenter.startCall();
      } else {
        presenter.requestWaitingRoomAccess(WaitingRoomAccessStatus.AskToJoin);
      }
    },

    checkToStart(): void {
      if (presenter.isWaitingRoomEnabled === null || presenter.waitingRoomMemberStatus === null) {
        return;
      }

      if (
        presenter.isWaitingRoomEnabled &&
        presenter.waitingRoomMemberStatus === WaitingRoomMemberChangedStatus.Approved
      ) {
        presenter.startCall();
      }

      if (
        !presenter.isWaitingRoomEnabled &&
        presenter.waitingRoomMemberStatus === WaitingRoomMemberChangedStatus.Requested
      ) {
        presenter.startCall();
      }
    },

    async startCall(): Promise<void> {
      presenter.isLoading = true;
      if (params.channelId && presenter.selfName) {
        const mediaParams = {
          withAudio: !props.devicesPresenter.isAudioMuted,
          withVideo: !props.devicesPresenter.isVideoMuted,
        };

        if (presenter.startCallRef.current) {
          presenter.startCallRef.current();
        }

        await Tasks.calls.joinToUnauthorizedCall({
          channelId: params.channelId,
          devicesIds: props.devicesPresenter.devicesIds,
          isWaitingRoomEnabled: presenter.isWaitingRoomEnabled || false,
          mediaPermissions: presenter.mediaPermissions,
          ...mediaParams,
        });
        callAnalytics.trackJoinedGuest();
        clearDeepLinkParameters();
      }
    },

    onMediaPermissionsChanged(value: Partial<IMediaPermissions>): void {
      presenter.mediaPermissions = { ...presenter.mediaPermissions, ...value };
    },

    async showAccessDeniedAndRedirect(): Promise<void> {
      await Tasks.app.showAlert({
        title: t('common_request_rejected'),
        text: t('calls_waiting_room_request_declined_message'),
        primaryText: t('common_ok'),
      });

      navigate(
        buildPath([RouterLink.Unauthorized, UnauthorizedRouts.CallInvitation], {
          channelId: params.channelId,
        }),
        { replace: true }
      );
    },

    async showCallFinishedAndRedirect(): Promise<void> {
      Tasks.app.showAlert({
        title: t('call_finished'),
      });

      navigate(
        buildPath([RouterLink.Unauthorized, UnauthorizedRouts.CallInvitation], {
          channelId: params.channelId,
        }),
        { replace: true }
      );
    },

    async showGuestsDisabledAndRedirect(): Promise<void> {
      if (presenter.isOpenedAlertGuestsDisabled) {
        return;
      }

      presenter.isOpenedAlertGuestsDisabled = true;

      await Tasks.app.showAlert({
        title: t('call_invitation_window_guests_disabled_header'),
        text: t('calls_waiting_room_request_declined_message'),
      });

      presenter.isOpenedAlertGuestsDisabled = false;

      navigate(
        buildPath([RouterLink.Unauthorized, UnauthorizedRouts.CallInvitation], {
          channelId: params.channelId,
        }),
        { replace: true }
      );
    },

    async onCancel(): Promise<void> {
      if (
        presenter.isWaitingRoomEnabled &&
        presenter.isUnauthorizedApiInitialized &&
        presenter.waitingRoomMemberStatus === WaitingRoomMemberChangedStatus.Requested
      ) {
        await presenter.requestWaitingRoomAccess(WaitingRoomAccessStatus.Cancel);
      }
      navigate(
        buildPath([RouterLink.Unauthorized, UnauthorizedRouts.CallInvitation], {
          channelId: params.channelId,
        }),
        { replace: true }
      );
    },

    initWaitingRoom(): void {
      presenter.disposeRoomListenerFunc = callModel.initWaitingRoomCallListener({
        onStatusCallChanged: async (data: IStatusCallChanged) => {
          presenter.isWaitingRoomEnabled = data.waitingRoomEnabled;
          /* init preview mode once */
          if (!presenter.previewDisplayMode) {
            presenter.previewDisplayMode =
              data.uiFeatures?.previewDisplayMode || PreviewDisplayMode.DefaultPreview;
          }

          if (data.status === CallStatus.Finished) {
            await presenter.showCallFinishedAndRedirect();
            return;
          }

          if (!data.guestsAllowed) {
            await presenter.showGuestsDisabledAndRedirect();
          }

          presenter.checkToStart();
        },
        onWaitingRoomMemberChanged: (data) => {
          presenter.waitingRoomMemberStatus = data.status;
          if (
            data.status === WaitingRoomMemberChangedStatus.Rejected &&
            presenter.isWaitingRoomEnabled === true
          ) {
            presenter.showAccessDeniedAndRedirect();
          }
          presenter.checkToStart();
        },
      });
    },

    disposeRoomListener(): void {
      if (presenter.disposeRoomListenerFunc) {
        presenter.disposeRoomListenerFunc();
        presenter.disposeRoomListenerFunc = null;
      }
    },
  }));

  useEffect(() => {
    initUnauthorizedCallsApi(ProjectUrls.wsCallsUrl);
    if (!presenter.disposeRoomListenerFunc) {
      presenter.initWaitingRoom();
    }
    presenter.isUnauthorizedApiInitialized = true;
    unauthorizedLogin(presenter.selfName, params.channelId);

    return () => {
      presenter.disposeRoomListener();
      unauthorizedWsCallsApiDisconnect();
      disposeUnauthorizedCallsApi();
      if (presenter.ongoingCall) {
        Entities.Calls.delete(presenter.ongoingCall.groupId, presenter.ongoingCall.channelId);
      }
    };
  }, []);

  useEffect(() => {
    if (presenter.ongoingCall) {
      presenter.disposeRoomListener();
    } else if (!presenter.disposeRoomListenerFunc && presenter.isUnauthorizedApiInitialized) {
      presenter.initWaitingRoom();
    }
  }, [!presenter.ongoingCall]);

  useEffect(() => {
    if (!presenter.selfName) {
      navigate(
        buildPath([RouterLink.Unauthorized, UnauthorizedRouts.CallInvitation], {
          channelId: params.channelId,
        }),
        { replace: true }
      );
    }
  }, [navigate, presenter.selfName]);

  return presenter;
};
